// src/pages/mallkiosk/VendorMenuScreen.js
// One vendor's full menu, large-format grid. Part of the core browse loop.
import React, { useState } from 'react';
import { useLanguage }  from '../../context/LanguageContext';
import { useMultiCart } from '../../context/MultiCartContext';
import ModifierSelector  from '../../components/ModifierSelector';
import BundleConfigurator from '../../components/BundleConfigurator';
import api from '../../services/api';
import { applyDietary, categoriesOf } from '../../utils/dietary';
import { DietaryPill, DietaryHiddenNote, UnverifiedTag } from '../../components/DietaryFilterUI';

export default function VendorMenuScreen({ vendorData, totals, diet = 'none', onClearDiet, onBack, onOpenCart, onCancelOrder }) {
  const { t, isAr, itemName } = useLanguage();
  const { carts, addItem, setQty } = useMultiCart();

  const [cat,          setCat]          = useState('All');
  const [search,       setSearch]       = useState('');
  const [modifierItem, setModifierItem] = useState(null);
  const [bundleItem,   setBundleItem]   = useState(null);
  const [showUnverified, setShowUnverified] = useState(false);

  const vendorId = vendorData.vendor.id;
  const { shown: items, unverified } = applyDietary(vendorData.menuItems || [], diet, showUnverified);
  const cats     = categoriesOf(items);
  const activeCat = cats.includes(cat) ? cat : 'All'; // a category with no visible items disappears
  const q        = search.trim().toLowerCase();
  const filtered = items
    .filter(i => activeCat === 'All' || i.category === activeCat)
    .filter(i => !q || i.name?.toLowerCase().includes(q) || (i.nameAr && i.nameAr.includes(search)));

  const getQty = (id) => (carts[vendorId] || []).find(i => i.id === id)?.qty || 0;

  const handleTap = async (item) => {
    if (item.itemType === 'BUNDLE') {
      // Listing payload is lightweight — fetch full item with nested
      // bundleComponents + their modifier groups before configuring.
      try {
        const r = await api.get(`/menu/${item.id}`);
        setBundleItem(r.data);
      } catch (_) {}
      return;
    }
    if (item.hasModifiers) { setModifierItem(item); return; }
    addItem(vendorId, item);
  };

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg)', direction: isAr ? 'rtl' : 'ltr' }}>

      {/* Header */}
      <div style={{ padding: '24px 40px 12px', display: 'flex', alignItems: 'center', gap: 18 }}>
        <button onClick={onBack} style={{
          width: 56, height: 56, borderRadius: 16, background: 'var(--bg3)', border: '1px solid var(--border)',
          color: 'var(--text)', fontSize: 26, cursor: 'pointer',
        }}>{isAr ? '→' : '←'}</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--text)' }}>
            {vendorData.vendor.emoji} {vendorData.vendor.name}
          </div>
          <div style={{ fontSize: 15, color: 'var(--text2)', marginTop: 2 }}>{t('Back to stalls', 'العودة إلى المتاجر')}</div>
        </div>
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder={t('🔍 Search menu...', '🔍 ابحث في القائمة...')} style={{
            width: 260, padding: '14px 20px', borderRadius: 14, fontSize: 17,
            background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none',
          }} />
        <button onClick={onCancelOrder} style={{
          padding: '12px 22px', borderRadius: 14, background: 'var(--bg3)', border: '1px solid var(--border)',
          color: 'var(--text2)', fontSize: 15, fontWeight: 600, cursor: 'pointer',
        }}>
          {t('✕ Start Over', '✕ البدء من جديد')}
        </button>
      </div>

      {diet !== 'none' && (
        <div style={{ padding: '0 40px 8px' }}>
          <DietaryPill preference={diet} isAr={isAr} large onClear={() => { onClearDiet?.(); setShowUnverified(false); }} />
        </div>
      )}

      {/* Category tabs */}
      <div style={{ display: 'flex', gap: 12, padding: '8px 40px 20px', overflowX: 'auto' }}>
        {cats.map(c => (
          <button key={c} onClick={() => setCat(c)} style={{
            padding: '14px 26px', borderRadius: 24, fontSize: 18, whiteSpace: 'nowrap', cursor: 'pointer', fontWeight: 700,
            background: activeCat === c ? 'var(--accent)' : 'var(--bg3)',
            color:      activeCat === c ? '#000' : 'var(--text2)',
            border:     `1px solid ${activeCat === c ? 'var(--accent)' : 'var(--border)'}`,
          }}>{c === 'All' ? t('All', 'الكل') : c}</button>
        ))}
      </div>

      {/* Item grid */}
      <div style={{
        flex: 1, minHeight: 0, minWidth: 0, overflowY: 'auto', padding: '0 40px 160px',
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gridAutoRows: 'min-content', gap: 24, alignContent: 'start',
      }}>
        {filtered.map(item => (
          <MenuItemCard
            key={item.id}
            item={item}
            qty={getQty(item.id)}
            onAdd={() => handleTap(item)}
            onDecrement={() => setQty(vendorId, item.id, getQty(item.id) - 1)}
            t={t}
            itemName={itemName}
            isAr={isAr}
          />
        ))}
        {filtered.length === 0 && (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 60, color: 'var(--text3)', fontSize: 20 }}>
            {t('No items found', 'لا توجد عناصر')}
          </div>
        )}
        {diet !== 'none' && (
          <div style={{ gridColumn: '1/-1' }}>
            <DietaryHiddenNote count={unverified.length} expanded={showUnverified} isAr={isAr} large
              onToggle={() => setShowUnverified(v => !v)} />
          </div>
        )}
      </div>

      {/* Floating cart button */}
      {totals.totalItems > 0 && (
        <button onClick={onOpenCart} style={{
          position: 'absolute', bottom: 32, insetInlineEnd: 40,
          padding: '22px 36px', borderRadius: 80, background: 'var(--accent)', color: 'var(--button-text)',
          border: 'none', fontSize: 22, fontWeight: 800, cursor: 'pointer',
          boxShadow: '0 8px 30px rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', gap: 12,
        }}>
          🛒 {totals.totalItems} · AED {totals.grandTotal.toFixed(2)}
        </button>
      )}

      {modifierItem && (
        <ModifierSelector
          item={modifierItem}
          onAdd={(configured) => { addItem(vendorId, configured); setModifierItem(null); }}
          onClose={() => setModifierItem(null)}
        />
      )}

      {bundleItem && (
        <BundleConfigurator
          item={bundleItem}
          onAdd={(configured) => { addItem(vendorId, configured); setBundleItem(null); }}
          onClose={() => setBundleItem(null)}
        />
      )}
    </div>
  );
}

// Broken/missing images fall back to the emoji placeholder instead of
// collapsing the card height (onError alone hides the <img> with no replacement).
function MenuItemCard({ item, qty, onAdd, onDecrement, t, itemName, isAr }) {
  const [imgError, setImgError] = useState(false);

  return (
    <div style={{
      background: 'var(--bg2)', borderRadius: 22, overflow: 'hidden',
      border: `2px solid ${qty > 0 ? 'var(--accent)' : 'var(--border)'}`,
    }}>
      {item.imageUrl && !imgError
        ? <img src={item.imageUrl} alt="" style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }} onError={() => setImgError(true)} />
        : <div style={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 70, background: 'var(--bg3)' }}>{item.emoji}</div>
      }
      <div style={{ padding: 18 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>{itemName(item)}{item.unverified && <UnverifiedTag isAr={isAr} large />}</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--accent)', marginBottom: 14 }}>
          {(item.hasModifiers || item.itemType === 'BUNDLE') ? `${t('from', 'من')} ` : ''}AED {item.price}
        </div>
        {qty > 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button onClick={onDecrement} style={{
              width: 52, height: 52, borderRadius: '50%', background: 'var(--bg4)', border: '1px solid var(--border)',
              color: 'var(--text)', fontSize: 26, cursor: 'pointer',
            }}>−</button>
            <span style={{ fontWeight: 800, fontSize: 24, color: 'var(--text)' }}>{qty}</span>
            <button onClick={onAdd} style={{
              width: 52, height: 52, borderRadius: '50%', background: 'var(--accent)', border: 'none',
              color: '#000', fontSize: 26, cursor: 'pointer',
            }}>+</button>
          </div>
        ) : (
          <button onClick={onAdd} style={{
            width: '100%', padding: '16px 0', borderRadius: 14, background: 'var(--accent)', border: 'none',
            color: '#000', fontWeight: 800, fontSize: 18, cursor: 'pointer',
          }}>
            {(item.hasModifiers || item.itemType === 'BUNDLE') ? t('Customise', 'تخصيص') : t('＋ Add', '＋ إضافة')}
          </button>
        )}
      </div>
    </div>
  );
}
